import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { tasksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { roleHome } from '../utils/roleHome';
import TaskEditDialog from '../components/TaskEditDialog';

/**
 * Direktlink auf ein Ticket (/task/:taskId) – Ziel der Links aus den
 * Benachrichtigungsmails. Lädt den Task und öffnet direkt den Ticket-Dialog.
 * Beim Schließen geht es in den Projekt-Workspace bzw. auf die Startseite.
 */
function TaskDeepLinkPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksAPI.getById(taskId);
      setTask(res.data);
      setError('');
    } catch (err) {
      console.error('[TASK-LINK] load error', err);
      setError(err.response?.status === 404
        ? 'Dieses Ticket existiert nicht (mehr) oder du hast keinen Zugriff darauf.'
        : 'Ticket konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const leave = () => {
    if (task?.project_id) navigate(`/pm/projects/${task.project_id}`, { replace: true });
    else navigate(roleHome(user), { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: 6, px: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate(roleHome(user), { replace: true })}>
          Zur Startseite
        </Button>
      </Box>
    );
  }

  return (
    <TaskEditDialog
      open
      onClose={leave}
      task={task}
      projectId={task?.project_id}
      onTaskUpdated={load}
    />
  );
}

export default TaskDeepLinkPage;
